import { uniq } from 'lodash';
import moment from "moment-timezone";
import { IVisualizationRange } from "~/interfaces/visualizationInterfaces";
import { DateRangeType, DateRangeUnitStrings } from "../../../formability/visualizationBuilder/enums/dateRangeType";
import { RangeMode } from "../../../formability/visualizationBuilder/enums/rangeMode";
import Selected from "../../../formability/visualizationBuilder/store/modules/selected";

export default class DateRangesUtils {
  ranges: IVisualizationRange[];
  dateRangeType;
  timezone: string;

  constructor(ranges) {
    this.ranges = ranges;
    this.dateRangeType = ranges[0].date_range_type;
    this.timezone = Selected.organization.get().time_zone;
  }

  index(range): number {
    return this.ranges.indexOf(range);
  }

  prevRange(range) {
    return this.ranges[this.index(range) - 1];
  }

  fromValue(range) {
    if (this.index(range) === 0) {
      return range.date_value ? range.date_value.tz(this.timezone) : null;
    }

    if (this.index(range) === 1) {
      const prevDate = moment(this.prevRange(range).date_value);
      return prevDate ? prevDate.tz(this.timezone) : null;
    }

    const prevVal = this.toValue(this.prevRange(range));

    return prevVal ? moment(prevVal).add(1, "days").tz(this.timezone) : null;
  }

  toValue(range) {
    if (!range.date_value) {
      return null;
    }

    if (range.inclusive) {
      if (this.dateRangeType === DateRangeType.date) {
        return moment(range.date_value).tz(this.timezone).endOf("day");
      } else if (this.dateRangeType === DateRangeType.month) {
        return moment(range.date_value).tz(this.timezone).endOf("month");
      } else if (this.dateRangeType === DateRangeType.year) {
        return moment(range.date_value).tz(this.timezone).endOf("year");
      }

      return range.date_value ? moment(range.date_value).tz(this.timezone) : null;
    }

    return moment(range.date_value).subtract(1, "day").tz(this.timezone);
  }

  rangeString(range) {
    let minVal = this.fromValue(range) ? this.formatDate(this.fromValue(range)) : null;
    let maxVal = this.toValue(range) ? this.formatDate(this.toValue(range)) : null;

    if (this.prevRange(range).mode === RangeMode.min) {
      minVal = 'Minimum Date';
    }

    if (range.mode === RangeMode.max) {
      maxVal = 'Maximum Date';
    }

    const sep = range.repeat ? '...' : '-';

    return `${minVal} ${sep} ${maxVal}`;
  }

  previousRangeSize(range) {
    if (!this.toValue(this.prevRange(range)) || !this.fromValue(this.prevRange(range))) {
      return 'unknown';
    }

    return moment(this.toValue(this.prevRange(range))).diff(moment(this.fromValue(this.prevRange(range))), this.getUnitString());
  }

  getUnitString() {
    return DateRangeUnitStrings[this.dateRangeType];
  }

  rangeBuckets(range) {
    if (!range.repeat) {
      return [];
    }

    const buckets = [];
    const prevRange = this.previousRangeSize(range);
    let prevValue = this.fromValue(range);

    if (!prevValue) {
      return [];
    }

    if (prevRange === 'unknown') {
      const endRange = this.toValue(range) ? this.formatDate(this.toValue(range)) : 'Maximum Date';
      buckets.push({
        range: `${this.formatDate(prevValue)} ... ${endRange}`,
      });

      return buckets;
    }

    // we don't know the buckets in this case
    if (!this.toValue(range)) {
      buckets.push({
        range: `${this.formatDate(prevValue)} ... Maximum Date`,
      });

      return buckets;
    }

    const step = prevRange;
    const numOfBuckets = this.toValue(range).diff(this.fromValue(range), this.getUnitString()) / prevRange;

    if (this.prevRange(this.prevRange(range)).mode === RangeMode.min) {
      return [{range: `${this.rangeString(range)}`}];
    }

    for (let j = 0; j < numOfBuckets; j++) {
      const end = moment(prevValue).add(step, this.getUnitString());
      const endString = moment(prevValue).add(step + 1, this.getUnitString()).subtract(1, "day")

      if (endString >= this.toValue(range) || prevValue >= this.toValue(range)) {
        break;
      }

      const subRangeObj = {
        range: `${this.formatDate(prevValue)} - ${this.formatDate(endString)}`
      }

      buckets.push(subRangeObj);
      prevValue = moment(end).add(1, this.getUnitString());
    }

    let sep, maxString;

    if (range.mode === RangeMode.number) {
      sep = '-';
      maxString = this.toValue(range)?.format('MMMM Do, YYYY');
    } else if (range.mode === RangeMode.max) {
      sep = "...";
      maxString = "Maximum Date";
    }

    buckets.push({
      range: `${this.formatDate(prevValue)} ${sep} ${maxString}`,
    });

    return buckets;
  }

  formatDate(d) {
    return d.tz(this.timezone).format('MMMM Do, YYYY');
  }

  liveRowBuckets(liveData) {
    if (!Array.isArray(liveData)) {
      return uniq(liveData["row_agg_c"]["buckets"].map(bucket => `${bucket.key}`));
    }

    return uniq(this.ranges[0].parent.parent.rowDataPointsArray.find(point => point.client_id === this.ranges[0].parent.client_id).liveRows.flat());
  }

  liveColBuckets(liveData) {
    if (!Array.isArray(liveData)) {
      return uniq(liveData["row_agg_c"]["buckets"].map(b => b.col_agg_c.buckets).flat().map(b => b.key));
    }

    return uniq(this.ranges[0].parent.parent.columnDataPointsArray.find(point => point.client_id === this.ranges[0].parent.client_id).liveCols.flat());
  }
}